import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Bell, MapPin, ShieldAlert } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { enablePush, getCurrentSubscription, getPushPermission, pushSupported } from '@/lib/push';

const SNOOZE_KEY = 'sisthoko_perm_reminder_snooze_until';
const SNOOZE_MS = 3 * 24 * 60 * 60 * 1000; // 3 days

function isPreviewHost() {
  if (typeof window === 'undefined') return true;
  const h = window.location.hostname;
  return h.startsWith('id-preview--') || h.startsWith('preview--')
    || h.endsWith('.lovableproject.com') || h.endsWith('.lovableproject-dev.com')
    || h.endsWith('.beta.lovable.dev') || h === 'localhost';
}

async function checkLocationGranted(): Promise<boolean> {
  try {
    if (!('permissions' in navigator)) return false;
    const status = await (navigator as any).permissions.query({ name: 'geolocation' });
    return status.state === 'granted';
  } catch {
    return false;
  }
}

/**
 * Foodpreneur permissions gate — nudges users to grant push + location.
 * Pops up on dashboard load if either is missing, snoozed 3 days on dismiss.
 * Never re-asks a permission that the OS has denied.
 */
export function PermissionsReminderGate() {
  const { profile } = useAuth();
  const [open, setOpen] = useState(false);
  const [needsPush, setNeedsPush] = useState(false);
  const [needsLocation, setNeedsLocation] = useState(false);
  const [pushBlocked, setPushBlocked] = useState(false);
  const [locationBlocked, setLocationBlocked] = useState(false);
  const [busy, setBusy] = useState<'push' | 'location' | null>(null);
  const checked = useRef(false);

  useEffect(() => {
    if (!profile?.id) return;
    if (isPreviewHost()) return;
    if (checked.current) return;
    checked.current = true;

    // Only foodpreneurs (skip admin/data_capturer/supplier — they have their own flows)
    const role = String(profile.role || '').toLowerCase();
    if (['admin', 'data_capturer', 'supplier'].includes(role)) return;

    const snoozedUntil = Number(localStorage.getItem(SNOOZE_KEY) || 0);
    if (snoozedUntil && Date.now() < snoozedUntil) return;

    (async () => {
      // Push status
      let pushOk = true;
      let pushDenied = false;
      if (pushSupported()) {
        const perm = await getPushPermission();
        if (perm === 'denied') { pushDenied = true; pushOk = true; /* can't ask */ }
        else if (perm !== 'granted') pushOk = false;
        else {
          const sub = await getCurrentSubscription();
          if (!sub) pushOk = false;
        }
      }

      // Location status
      const locGranted = await checkLocationGranted();
      let locDenied = false;
      try {
        const st = await (navigator as any).permissions.query({ name: 'geolocation' });
        locDenied = st.state === 'denied';
      } catch {}
      const locOk = locGranted || locDenied; // denied → can't re-ask, skip

      const needP = !pushOk && !pushDenied;
      const needL = !locGranted && !locDenied;

      setNeedsPush(needP);
      setNeedsLocation(needL);
      setPushBlocked(pushDenied);
      setLocationBlocked(locDenied);

      if (needP || needL) {
        setTimeout(() => setOpen(true), 1200);
      }
    })();
  }, [profile?.id]);

  async function handleEnablePush() {
    setBusy('push');
    const result = await enablePush();
    setBusy(null);
    if (!result.ok) {
      toast.error('Could not enable notifications', { description: result.error });
      return;
    }
    toast.success('Notifications enabled');
    setNeedsPush(false);
    if (!needsLocation) setOpen(false);
  }

  async function handleEnableLocation() {
    setBusy('location');
    try {
      await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 15000, enableHighAccuracy: false });
      });
      toast.success('Location enabled');
      setNeedsLocation(false);
      if (!needsPush) setOpen(false);
    } catch (e: any) {
      toast.error('Could not enable location', { description: e?.message });
    } finally {
      setBusy(null);
    }
  }

  function handleDismiss() {
    localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS));
    setOpen(false);
  }

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleDismiss(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mx-auto h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center mb-2">
            <ShieldAlert className="h-7 w-7 text-primary" />
          </div>
          <DialogTitle className="text-center text-xl">Finish setting up Sis Thoko</DialogTitle>
          <DialogDescription className="text-center">
            We need a couple of permissions so the platform can actually work for you.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {needsPush && (
            <div className="flex items-start gap-3 rounded-lg border p-3">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <Bell className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm">Notifications</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Get pinged the moment a winning bid lands — even when the app is closed.
                </p>
              </div>
              <Button size="sm" onClick={handleEnablePush} disabled={busy === 'push'}>
                {busy === 'push' ? '…' : 'Allow'}
              </Button>
            </div>
          )}

          {needsLocation && (
            <div className="flex items-start gap-3 rounded-lg border p-3">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <MapPin className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm">Location</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Used to match you with nearby wholesalers and calculate delivery.
                </p>
              </div>
              <Button size="sm" onClick={handleEnableLocation} disabled={busy === 'location'}>
                {busy === 'location' ? '…' : 'Allow'}
              </Button>
            </div>
          )}

          {(pushBlocked || locationBlocked) && (
            <p className="text-xs text-muted-foreground text-center pt-1">
              {pushBlocked && locationBlocked
                ? 'Notifications and location are blocked in your browser. Enable them in browser site settings.'
                : pushBlocked
                  ? 'Notifications are blocked in your browser. Enable them in browser site settings.'
                  : 'Location is blocked in your browser. Enable it in browser site settings.'}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={handleDismiss} className="w-full">
            Remind me later
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
